import { useCallback, useEffect, useState } from 'react';
import { Home } from 'lucide-react';
import TankMovementForm from './TankMovementForm';
import TankCertificationForm from './/TankCertificationForm';
import FinalQCChecklistForm from './FinalQCChecklistForm';
import EngineeringForm from './EngineeringForm';
import PreInspectionForm from './PreInspectionForm';
import PaintQualityInspectionForm from './PaintQualityInspectionForm';
import PreCoatingBatchQualityForm from './PreCoatingBatchQualityForm';
import VesselFailureReportForm from './VesselFailureReportForm';
import CustomerComplaintForm from './CustomerComplaintForm';
import UltrasonicDailyCheckForm from './UltrasonicDailyCheckForm';
import BumpTestForm from './BumpTestForm';
import GasFreeCheckForm from './GasFreeCheckForm';
import CGScrapDeliveryNoteForm from './CGScrapDeliveryNoteForm';
import PVSPageLayout from './PVSPageLayout';
import { canAccess } from '../config/userPermissions';
import fetchUltrasonicDailyCheckStatus from '../services/ultrasonicDailyCheckStatusService';
import fetchBumpTestStatus from '../services/bumpTestStatusService';

const FORM_CATALOG = [
  { key: 'tank-movement', title: 'Tank Movement Form', description: 'Record tank movements and transfers' },
  { key: 'tank-certification', title: 'Tank Certification', description: 'Manage tank certifications', requiresUltrasonicCheck: true },
  { key: 'final-qc-checklist', title: 'Final QC Checklist', description: 'Quality control inspection form' },
  { key: 'engineering', title: 'Engineering Form', description: 'Complete engineering documentation' },
  { key: 'pre-inspection', title: 'Pre Inspection Form', description: 'Vessel pre-inspection documentation' },
  { key: 'paint-quality-inspection', title: 'Paint Quality Inspection', description: 'Paint QC gate documentation' },
  { key: 'pre-coating-batch', title: 'Pre Coating Batch Quality', description: 'Batch pre-coating QC documentation' },
  { key: 'vessel-failure-report', title: 'Vessel Failure Report', description: 'Nonconformance & failure documentation' },
  { key: 'customer-complaint', title: 'Customer Complaint & Appeal', description: 'Complaint, appeal & corrective action log' },
  { key: 'ultrasonic-daily-check', title: 'Ultrasonic Daily Check', description: 'Daily ultrasonic calibration check', dailyCheckForm: true },
  { key: 'bump-test', title: 'Bump Test', description: 'Daily gas detector bump test', dailyCheckForm: true },
  { key: 'gas-free-check', title: 'Gas Free Check', description: 'Vessel gas free (LEL) verification', requiresBumpTest: true },
  { key: 'cg-scrap-delivery-note', title: 'CG Scrap Delivery Note', description: 'Calor scrap vessel delivery to EMR Bradford' },
];

const PVSForms = ({ onReturnToPVSHomePage, initialFormKey, onInitialFormConsumed }) => {
  const [currentForm, setCurrentForm] = useState(null);
  const [gateStatus, setGateStatus] = useState(null);
  const [bumpGateStatus, setBumpGateStatus] = useState(null);
  const [bumpGateLoaded, setBumpGateLoaded] = useState(false);
  const [gateMessage, setGateMessage] = useState('');

  const refreshGateStatus = useCallback(async () => {
    try {
      const status = await fetchUltrasonicDailyCheckStatus();
      setGateStatus(status);
      return status;
    } catch (error) {
      console.error('Failed to load ultrasonic daily check status:', error);
      setGateStatus(null);
      return null;
    }
  }, []);

  const refreshBumpGateStatus = useCallback(async () => {
    try {
      const status = await fetchBumpTestStatus();
      setBumpGateStatus(status);
      setBumpGateLoaded(true);
      return status;
    } catch (error) {
      console.error('Failed to load bump test status:', error);
      // Fail closed: treat as not completed so Gas Free Check stays locked.
      setBumpGateStatus({
        completedToday: false,
        bumpTestAvailable: true,
        gasFreeCheckUnlocked: false,
        completedBy: null,
        completedAt: null,
        databaseAvailable: false,
      });
      setBumpGateLoaded(true);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshGateStatus();
    refreshBumpGateStatus();
  }, [refreshGateStatus, refreshBumpGateStatus]);

  const isFormLocked = useCallback((formKey) => {
    if (formKey === 'ultrasonic-daily-check') {
      if (!gateStatus) return false;
      return gateStatus.completedToday;
    }
    if (formKey === 'tank-certification') {
      if (!gateStatus) return false;
      return !gateStatus.calorAndTankCertUnlocked;
    }
    if (formKey === 'bump-test') {
      // Same as ultrasonic: once completed today, shade out / unclickable.
      if (!bumpGateLoaded) return false;
      return Boolean(bumpGateStatus?.completedToday);
    }
    if (formKey === 'gas-free-check') {
      // Same rule as tank-certification vs ultrasonic: locked until daily gate is done.
      // Fail closed while loading or if status cannot be confirmed.
      if (!bumpGateLoaded || !bumpGateStatus) return true;
      return !bumpGateStatus.gasFreeCheckUnlocked;
    }
    return false;
  }, [gateStatus, bumpGateStatus, bumpGateLoaded]);

  const getLockMessage = (formKey) => {
    if (formKey === 'ultrasonic-daily-check') {
      return 'Already completed today — available again after midnight.';
    }
    if (formKey === 'tank-certification') {
      return 'Complete the Ultrasonic Daily Check first.';
    }
    if (formKey === 'bump-test') {
      return 'Already completed today — available again after midnight.';
    }
    if (formKey === 'gas-free-check') {
      if (!bumpGateLoaded || !bumpGateStatus) {
        return 'Checking bump test status…';
      }
      return 'Complete the Bump Test first.';
    }
    return '';
  };

  const openForm = (formKey) => {
    if (isFormLocked(formKey)) {
      setGateMessage(getLockMessage(formKey));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setGateMessage('');
    setCurrentForm(formKey);
  };

  useEffect(() => {
    if (!initialFormKey || !canAccess(initialFormKey)) {
      if (initialFormKey && typeof onInitialFormConsumed === 'function') {
        onInitialFormConsumed();
      }
      return;
    }

    let cancelled = false;

    const tryOpenInitialForm = async () => {
      const ultrasonicStatus = gateStatus || await refreshGateStatus();
      const bumpStatus = bumpGateStatus || await refreshBumpGateStatus();
      if (cancelled) return;

      if (initialFormKey === 'tank-certification' && ultrasonicStatus && !ultrasonicStatus.calorAndTankCertUnlocked) {
        setGateMessage('Complete the Ultrasonic Daily Check before opening Tank Certification.');
        setCurrentForm(null);
      } else if (initialFormKey === 'ultrasonic-daily-check' && ultrasonicStatus?.completedToday) {
        setGateMessage('Ultrasonic Daily Check has already been completed today.');
        setCurrentForm(null);
      } else if (initialFormKey === 'gas-free-check' && (!bumpStatus || !bumpStatus.gasFreeCheckUnlocked)) {
        setGateMessage('Complete the Bump Test before opening Gas Free Check.');
        setCurrentForm(null);
      } else if (initialFormKey === 'bump-test' && bumpStatus?.completedToday) {
        setGateMessage('Bump Test has already been completed today.');
        setCurrentForm(null);
      } else if (!isFormLocked(initialFormKey)) {
        setCurrentForm(initialFormKey);
      }

      if (typeof onInitialFormConsumed === 'function') {
        onInitialFormConsumed();
      }
    };

    tryOpenInitialForm();

    return () => {
      cancelled = true;
    };
  }, [initialFormKey, onInitialFormConsumed, gateStatus, bumpGateStatus, refreshGateStatus, refreshBumpGateStatus, isFormLocked]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentForm]);

  const renderActiveForm = () => {
    if (currentForm && !canAccess(currentForm)) {
      return renderFormSelector();
    }

    if (currentForm === 'tank-certification' && isFormLocked('tank-certification')) {
      return renderFormSelector();
    }

    if (currentForm === 'ultrasonic-daily-check' && isFormLocked('ultrasonic-daily-check')) {
      return renderFormSelector();
    }

    if (currentForm === 'bump-test' && isFormLocked('bump-test')) {
      return renderFormSelector();
    }

    if (currentForm === 'gas-free-check' && isFormLocked('gas-free-check')) {
      return renderFormSelector();
    }

    switch (currentForm) {
      case 'tank-movement':
        return <TankMovementForm onBack={() => setCurrentForm(null)} />;
      case 'tank-certification':
        return <TankCertificationForm onBack={() => setCurrentForm(null)} />;
      case 'final-qc-checklist':
        return <FinalQCChecklistForm onBack={() => setCurrentForm(null)} />;
      case 'engineering':
        return <EngineeringForm onBack={() => setCurrentForm(null)} />;
      case 'pre-inspection':
        return <PreInspectionForm onBack={() => setCurrentForm(null)} />;
      case 'paint-quality-inspection':
        return <PaintQualityInspectionForm onBack={() => setCurrentForm(null)} />;
      case 'pre-coating-batch':
        return <PreCoatingBatchQualityForm onBack={() => setCurrentForm(null)} />;
      case 'vessel-failure-report':
        return <VesselFailureReportForm onBack={() => setCurrentForm(null)} />;
      case 'customer-complaint':
        return <CustomerComplaintForm onBack={() => setCurrentForm(null)} />;
      case 'ultrasonic-daily-check':
        return (
          <UltrasonicDailyCheckForm
            onBack={() => {
              setCurrentForm(null);
              refreshGateStatus();
            }}
            gateStatus={gateStatus}
            onCompleted={refreshGateStatus}
          />
        );
      case 'bump-test':
        return (
          <BumpTestForm
            onBack={() => {
              setCurrentForm(null);
              refreshBumpGateStatus();
            }}
            gateStatus={bumpGateStatus}
            onCompleted={refreshBumpGateStatus}
          />
        );
      case 'gas-free-check':
        return (
          <GasFreeCheckForm
            onBack={() => setCurrentForm(null)}
            bumpGateStatus={bumpGateStatus}
          />
        );
      case 'cg-scrap-delivery-note':
        return <CGScrapDeliveryNoteForm onBack={() => setCurrentForm(null)} />;
      default:
        return renderFormSelector();
    }
  };

  const renderFormSelector = () => (
    <PVSPageLayout className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-8 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">PVS Forms</h1>
          <button
            onClick={onReturnToPVSHomePage}
            className="flex items-center justify-center w-12 h-12 flex-shrink-0 pvs-glass pvs-glass-hover"
            title="Return to Homepage"
          >
            <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
          </button>
        </div>

        {gateStatus && !gateStatus.calorAndTankCertUnlocked && (
          <div className="mb-6 p-4 rounded-lg border border-amber-500/40 bg-amber-900/20 text-amber-100">
            <p className="text-sm font-medium">
              Ultrasonic daily check required — Tank Certification is locked until today&apos;s check is submitted.
            </p>
          </div>
        )}

        {gateStatus?.testingBypassActive && (
          <div className="mb-6 p-4 rounded-lg border border-purple-500/40 bg-purple-900/20 text-purple-100">
            <p className="text-sm font-medium">
              Testing mode — certificate numbering is disabled, so the Ultrasonic Daily Check requirement is
              bypassed.
            </p>
          </div>
        )}

        {gateStatus?.completedToday && gateStatus.completedBy && (
          <div className="mb-6 p-4 rounded-lg border border-green-500/30 bg-green-900/20 text-green-100">
            <p className="text-sm">
              Today&apos;s ultrasonic check completed by {gateStatus.completedBy}.
            </p>
          </div>
        )}

        {bumpGateLoaded && bumpGateStatus && !bumpGateStatus.gasFreeCheckUnlocked && (
          <div className="mb-6 p-4 rounded-lg border border-amber-500/40 bg-amber-900/20 text-amber-100">
            <p className="text-sm font-medium">
              Bump test required — Gas Free Check is locked until today&apos;s bump test is submitted.
            </p>
          </div>
        )}

        {bumpGateStatus?.completedToday && bumpGateStatus.completedBy && (
          <div className="mb-6 p-4 rounded-lg border border-green-500/30 bg-green-900/20 text-green-100">
            <p className="text-sm">
              Today&apos;s bump test completed by {bumpGateStatus.completedBy}.
            </p>
          </div>
        )}

        {gateMessage && (
          <div className="mb-6 p-4 rounded-lg border border-red-500/30 bg-red-900/20 text-red-200">
            <p className="text-sm font-medium">{gateMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FORM_CATALOG.filter((form) => canAccess(form.key)).map((form) => {
            const locked = isFormLocked(form.key);
            return (
              <button
                key={form.key}
                onClick={() => openForm(form.key)}
                disabled={locked}
                className={`p-6 pvs-glass text-left ${
                  locked ? 'opacity-50 cursor-not-allowed' : 'pvs-glass-hover'
                }`}
              >
                <h2 className="text-xl font-semibold text-white mb-2">{form.title}</h2>
                <p className="text-pvs-text-muted">{form.description}</p>
                {locked && (
                  <p className="text-xs text-amber-300 mt-3">{getLockMessage(form.key)}</p>
                )}
              </button>
            );
          })}
          {FORM_CATALOG.filter((form) => canAccess(form.key)).length === 0 && (
            <p className="text-pvs-text-muted col-span-full text-center py-4">
              No forms available for your account.
            </p>
          )}
        </div>
      </div>
    </PVSPageLayout>
  );

  return renderActiveForm();
};

export default PVSForms;
